import { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FORM_TYPE_CREATE_DATA_SOURCE } from 'js/constants/formTypes';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { updateFormData, setFormStageIndex, setFormError } from 'js/actions';
import UIUtils from 'js/lib/UIUtils';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { STORAGE_TYPE_PARAM } from './SqlRunnerSourceForm.helpers';

const FORM_TYPE = FORM_TYPE_CREATE_DATA_SOURCE;
const CONNECTION = CONNECTOR_CODE_SQL_RUNNER;

/**
 * Handles edit mode: selects source/credentials/config from store and hydrates
 * the SQL Runner form when the source is loaded, then sets form stage to Configure.
 */
export function useSqlRunnerEditHydration(sourceId: number | undefined) {
  const dispatch = useAppDispatch();
  const editHydratedRef = useRef(false);

  const editSource = useSelector((state: { entities?: { sources?: Record<string, unknown> } }) =>
    sourceId != null && state.entities?.sources
      ? (state.entities.sources[String(sourceId)] as Record<string, unknown> | undefined)
      : null,
  );

  const credentialsById = useSelector(
    (state: { entities?: { credentials?: Record<string, unknown> } }) =>
      (state.entities?.credentials ?? {}) as Record<string, unknown>,
  );
  const sourceConfigById = useSelector(
    (state: { paging?: { sourceConfigById?: Record<string, unknown> } }) =>
      state.paging?.sourceConfigById ?? {},
  );

  useEffect(() => {
    if (sourceId == null || !editSource || !editSource.source_config || editHydratedRef.current)
      return;

    const fields = sourceConfigById[CONNECTION] as
      | { name?: string; path_in_resource?: string }[]
      | undefined;
    const { templatizedScriptConfig, scriptCredentialsData } =
      UIUtils.buildTemplatizedScriptConfigFromSource(
        editSource,
        Array.isArray(fields) ? fields : undefined,
        credentialsById,
      );
    const sourceType = editSource.source_type as string | undefined;
    const templatizedWithSourceType =
      sourceType != null
        ? { ...templatizedScriptConfig, [STORAGE_TYPE_PARAM]: sourceType }
        : templatizedScriptConfig;

    let credentialId = (editSource.data_credentials_id ?? editSource.data_credentials) as
      | number
      | string
      | undefined;

    const credential = credentialId ? credentialsById[String(credentialId)] : null;

    if (credentialId != null && !credential) {
      dispatch(
        setFormError(
          FORM_TYPE,
          'Original credential no longer available. Please select a new credential.',
        ),
      );
      credentialId = undefined;
    }

    const sourceConfigFromApi = editSource.source_config as Record<string, unknown>;
    let sourceConfig = { ...sourceConfigFromApi };
    if (sourceConfigFromApi['start.cron'] == null) {
      const scriptConfig = editSource.script_config as
        | {
            config?: Record<string, unknown>;
            parameters?: Record<string, unknown>;
          }
        | undefined;
      const cronFromScript =
        (scriptConfig?.config?.['parameters.script.start.cron'] as string | undefined) ??
        (scriptConfig?.parameters?.['script.start.cron'] as string | undefined) ??
        (scriptConfig?.parameters?.['parameters.script.start.cron'] as string | undefined);
      if (cronFromScript != null) {
        sourceConfig = { ...sourceConfigFromApi, 'start.cron': cronFromScript };
      }
    }
    const formData: Record<string, unknown> = {
      selectedConnection: CONNECTION,
      sourceConfig,
      selectedCredentialId: credentialId ?? null,
      selectedCredentialInfo: credential ?? null,
      sourceName: (editSource.name as string) ?? '',
      sourceDescription: (editSource.description as string) ?? '',
      ...templatizedWithSourceType,
      ...(scriptCredentialsData as Record<string, unknown>),
    };

    editHydratedRef.current = true;
    dispatch(updateFormData(FORM_TYPE, formData));
    dispatch(setFormStageIndex(FORM_TYPE, 2));
  }, [sourceId, editSource, sourceConfigById, credentialsById, dispatch]);
}
